import { useEffect, useState } from "react";
import { GiBarn } from "react-icons/gi";
import { LuCopyPlus } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import ModalCadastroAnimal from "../../components/modais/ModalCadastroAnimal";
import ModalCadastroFazenda from "../../components/modais/ModalCadastroFazenda";
import ModalCadastroPasto from "../../components/modais/ModalCadastroPasto";
import { api } from "../../services/api";
import { repositorioFazenda } from "../../sync/repositories/fazendas";
import { repositorioAnimal, repositorioAnimalLote, repositorioLote, repositorioPasto } from "../../sync/repositories/pecuaria";

const AJUDA = {
  titulo: "Cadastro de Lotes",
  descricao:
    "Um lote agrupa vários animais que são manejados juntos, na mesma fase e no mesmo pasto. Ao criar um lote você escolhe os animais que entram nele e o peso de entrada de cada um; a partir daí, pesagens e rotações de pasto passam a ser acompanhadas por lote.",
  secoes: [
    {
      titulo: "Dados do Lote",
      campos: [
        { nome: "Nome do Lote", obrigatorio: true, explicacao: "Nome que identifica o lote (ex.: 'Lote 01/2024')." },
        { nome: "Fazenda", obrigatorio: true, explicacao: "Fazenda onde o lote está. Não pode ser alterada depois que o lote é criado." },
        { nome: "Pasto Inicial", obrigatorio: true, explicacao: "Pasto onde o lote entra no momento do cadastro. Movimentações posteriores entre pastos são registradas na tela de Rotação de Pastos." },
      ],
    },
    {
      titulo: "Datas",
      campos: [
        { nome: "Data de Entrada", obrigatorio: true, explicacao: "Data em que o lote foi formado. Não pode ser alterada depois que o lote é criado." },
        { nome: "Data de Saída Prevista", obrigatorio: false, explicacao: "Data estimada para o encerramento do lote (ex.: previsão de venda/abate). Se essa data passar sem o lote ser finalizado, ele aparece como 'Prazo Excedido' na lista." },
      ],
    },
    {
      titulo: "Metas",
      campos: [
        { nome: "Peso Objetivo", obrigatorio: true, explicacao: "Peso que se espera que os animais do lote atinjam." },
        { nome: "GMD Previsto", obrigatorio: true, explicacao: "Ganho Médio Diário esperado (kg/dia)." },
        { nome: "Fase", obrigatorio: true, explicacao: "Etapa produtiva do lote: Cria (0-8 meses), Recria (8-15 meses) ou Engorda (15+ meses)." },
      ],
    },
    {
      titulo: "Seleção de Animais",
      campos: [
        { nome: "Selecionar Animais", obrigatorio: true, explicacao: "Só aparece no cadastro de um lote novo. Escolha os animais disponíveis (sem lote e ativos) e informe o peso de entrada de cada um selecionado." },
      ],
    },
  ],
  dicas: [
    "Cadastrar, editar ou excluir um lote exige conexão com a internet — apenas a consulta funciona offline.",
    "Um lote com animais ativos não pode ser excluído.",
    "A seleção de animais só é exibida no cadastro de um lote novo; na edição, fazenda e data de entrada ficam bloqueadas e os animais do lote são geridos em outra tela.",
  ],
};

export default function GestaoLotes() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [lotes, setLotes] = useState([]);
  const [lotesFiltrados, setLotesFiltrados] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  
  // Dados para os selects
  const [fazendas, setFazendas] = useState([]);
  const [pastos, setPastos] = useState([]);
  const [allAnimals, setAllAnimals] = useState([]);
  const [selectedAnimals, setSelectedAnimals] = useState([]);
  const [mostrarModalFazenda, setMostrarModalFazenda] = useState(false);
  const [mostrarModalPasto, setMostrarModalPasto] = useState(false);
  const [mostrarModalAnimal, setMostrarModalAnimal] = useState(false);
  const [buscaAnimal, setBuscaAnimal] = useState("");
  
  // Filtros da tabela
  const [filtros, setFiltros] = useState({
    busca: "",
    fase: ""
  });
  
  // Formulário
  const [formData, setFormData] = useState({
    nome: "",
    fazenda: "",
    pasto: "",
    data_entrada: "",
    data_saida_prevista: "",
    peso_objetivo: "",
    gmd_previsto: "",
    fase: "CR",
    pesoEntrada: {},
  });

  // Fases disponíveis
  const fases = [
    { value: "CR", label: "🐄 Cria (0-8 meses)", descricao: "Animais jovens em fase de aleitamento" },
    { value: "RC", label: "📈 Recria (8-15 meses)", descricao: "Crescimento e desenvolvimento" },
    { value: "EG", label: "⚖️ Engorda (15+ meses)", descricao: "Terminação para abate" }
  ];

  // Carregar lotes
  const carregarLotes = async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioLote.listar();
      const dados = response.data.results || response.data;

      // Buscar informações adicionais de cada lote
      const lotesComInfo = await Promise.all(
        dados.map(async (lote) => {
          try {
            const animaisResponse = await repositorioAnimalLote.listar(`?lote=${lote.id}`);
            const animaisNoLote = animaisResponse.data.results || animaisResponse.data;
            return {
              ...lote,
              total_animais: animaisNoLote.length,
              animais_ativos: animaisNoLote.filter(a => !a.data_saida).length
            };
          } catch {
            return { ...lote, total_animais: 0, animais_ativos: 0 };
          }
        })
      );
      
      setLotes(lotesComInfo);
      setLotesFiltrados(lotesComInfo);
    } catch (error) {
      console.error("Erro ao carregar lotes:", error);
      alert("Erro ao carregar lista de lotes");
    } finally {
      setCarregandoLista(false);
    }
  };

  // Carregar fazendas
  const carregarFazendas = async () => {
    try {
      const response = await repositorioFazenda.listar();
      setFazendas(response.data.results || response.data);
    } catch (error) {
      console.error("Erro ao carregar fazendas:", error);
    }
  };

  // Carregar animais da fazenda selecionada
  useEffect(() => {
    if (!formData.fazenda) {
      setAllAnimals([]);
      return;
    }
    
    const fetchAnimals = async () => {
      try {
        const response = await repositorioAnimal.listar(`?fazenda=${formData.fazenda}`);
        const dados = response.data.results || response.data;
        setAllAnimals(dados);
      } catch (error) {
        console.error("Erro ao carregar animais:", error);
      }
    };
    
    fetchAnimals();
  }, [formData.fazenda]);

  // Carregar pastos da fazenda selecionada
  useEffect(() => {
    if (!formData.fazenda) {
      setPastos([]);
      return;
    }
    
    const fetchPastos = async () => {
      try {
        const response = await repositorioPasto.listar(`?fazenda=${formData.fazenda}`);
        setPastos(response.data.results || response.data);
      } catch (error) {
        console.error("Erro ao carregar pastos:", error);
      }
    };
    
    fetchPastos();
  }, [formData.fazenda]);

  // Carregar dados iniciais
  useEffect(() => {
    carregarLotes();
    carregarFazendas();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validarFormulario = () => {
    if (!formData.nome.trim()) {
      alert("Informe o nome do lote.");
      return false;
    }
    if (!formData.fazenda) {
      alert("Selecione uma fazenda.");
      return false;
    }
    if (!formData.pasto) {
      alert("Selecione um pasto inicial.");
      return false;
    }
    if (!formData.data_entrada) {
      alert("Informe a data de entrada.");
      return false;
    }
    if (!formData.peso_objetivo || Number(formData.peso_objetivo) <= 0) {
      alert("Informe um peso objetivo válido.");
      return false;
    }
    if (!formData.gmd_previsto || Number(formData.gmd_previsto) <= 0) {
      alert("Informe o GMD previsto.");
      return false;
    }
    if (selectedAnimals.length === 0 && !editando) {
      alert("Selecione pelo menos um animal para o lote.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    setLoading(true);
    
    try {
      if (editando) {
        // Editar lote existente
        const response = await api.patch(`/pecuaria/lotes/${editandoId}/`, {
          nome: formData.nome,
          data_saida_prevista: formData.data_saida_prevista || null,
          peso_objetivo: Number(formData.peso_objetivo),
          gmd_previsto: Number(formData.gmd_previsto),
          fase: formData.fase,
          pasto: formData.pasto,
        });
        
        if (response.status === 200) {
          alert("Lote atualizado com sucesso!");
          handleCancel();
          carregarLotes();
        }
      } else {
        // Criar novo lote
        const lotePayload = {
          nome: formData.nome,
          fazenda: Number(formData.fazenda),
          data_entrada: formData.data_entrada,
          data_saida_prevista: formData.data_saida_prevista || null,
          peso_objetivo: Number(formData.peso_objetivo),
          gmd_previsto: Number(formData.gmd_previsto),
          fase: formData.fase,
          pasto: formData.pasto,
        };
        
        const responseLote = await api.post("/pecuaria/lotes/", lotePayload);
        const loteCriado = responseLote.data;
        const loteId = loteCriado.id || loteCriado.pk;
        
        // Adicionar animais ao lote
        for (const animalId of selectedAnimals) {
          const animalPayload = {
            animal: animalId,
            lote: loteId,
            peso_entrada: Number(formData.pesoEntrada[animalId] || 0),
            data_entrada: formData.data_entrada
          };
          await api.post("/pecuaria/animal-lote/", animalPayload);
        }
        
        alert("Lote cadastrado com sucesso!");
        handleCancel();
        carregarLotes();
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (lote) => {
    setFormData({
      nome: lote.nome || "",
      fazenda: lote.fazenda?.id || lote.fazenda || "",
      pasto: lote.pasto?.id || lote.pasto || "",
      data_entrada: lote.data_entrada || "",
      data_saida_prevista: lote.data_saida_prevista || "",
      peso_objetivo: lote.peso_objetivo || "",
      gmd_previsto: lote.gmd_previsto || "",
      fase: lote.fase || "CR",
      pesoEntrada: {},
    });
    setSelectedAnimals([]);
    setEditando(true);
    setEditandoId(lote.id);
    setMostrarFormulario(true);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovoLote = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      nome: "",
      fazenda: "",
      pasto: "",
      data_entrada: "",
      data_saida_prevista: "",
      peso_objetivo: "",
      gmd_previsto: "",
      fase: "CR",
      pesoEntrada: {},
    });
    setSelectedAnimals([]);
    setBuscaAnimal("");
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id, temAnimaisAtivos) => {
    if (temAnimaisAtivos) {
      alert("Não é possível excluir um lote que contém animais ativos.");
      return;
    }
    
    if (!window.confirm("Tem certeza que deseja excluir este lote? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await api.delete(`/pecuaria/lotes/${id}/`);
      alert("Lote excluído com sucesso!");
      carregarLotes();
      
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir lote.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = [...lotes];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(l => 
        l.nome?.toLowerCase().includes(buscaLower) ||
        l.fazenda_nome?.toLowerCase().includes(buscaLower)
      );
    }
    
    if (filtros.fase) {
      filtrados = filtrados.filter(l => l.fase === filtros.fase);
    }
    
    setLotesFiltrados(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "", fase: "" });
    setLotesFiltrados(lotes);
  };

  const getFaseLabel = (fase) => {
    const faseMap = { CR: "Cria", RC: "Recria", EG: "Engorda" };
    return faseMap[fase] || fase;
  };

  const getStatusLote = (lote) => {
    if (lote.data_saida) return { label: "Finalizado", color: "bg-gray-100 text-gray-800" };
    if (lote.data_saida_prevista) {
      const hoje = new Date();
      const saidaPrevista = new Date(lote.data_saida_prevista);
      if (saidaPrevista < hoje) return { label: "Prazo Excedido", color: "bg-red-100 text-red-800" };
    }
    return { label: "Ativo", color: "bg-green-100 text-green-800" };
  };

  // Filtrar animais disponíveis (não estão em lote)
  const animaisDisponiveis = allAnimals.filter(
    (animal) => !animal.lote_atual && animal.status === "A"
  );

  const animaisFiltradosBusca = animaisDisponiveis.filter(animal =>
    animal.nome?.toLowerCase().includes(buscaAnimal.toLowerCase()) ||
    animal.sisbov?.toLowerCase().includes(buscaAnimal.toLowerCase()) ||
    animal.numero_identificacao?.toLowerCase().includes(buscaAnimal.toLowerCase())
  );

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Lotes <GiBarn size={38}/></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovoLote}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novo Lote
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Lote" : "Cadastrar Novo Lote"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Linha 1: Nome, Fazenda, Pasto */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Nome do Lote *</label>
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Ex: Lote 01/2024"
                      required
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Fazenda *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="fazenda"
                        value={formData.fazenda}
                        onChange={handleChange}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                        disabled={editando}
                      >
                        <option value="">Selecione uma fazenda</option>
                        {fazendas.map(f => (
                          <option key={f.id} value={f.id}>{f.nome}</option>
                        ))}
                      </select>
                      {!editando && (
                        <button
                          type="button"
                          onClick={() => setMostrarModalFazenda(true)}
                          title="Cadastrar nova fazenda"
                          className="p-2 hover:bg-gray-200 rounded"
                        >
                          <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Pasto Inicial *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="pasto"
                        value={formData.pasto}
                        onChange={handleChange}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                        disabled={!formData.fazenda}
                      >
                        <option value="">Selecione um pasto</option>
                        {pastos.map(p => (
                          <option key={p.id} value={p.id}>{p.nome}</option>
                        ))}
                      </select>
                      <button
                          type="button"
                          onClick={() => setMostrarModalPasto(true)}
                          disabled={!formData.fazenda}
                          title={formData.fazenda ? "Cadastrar novo pasto" : "Selecione uma fazenda primeiro"}
                          className={`p-2 rounded ${formData.fazenda ? "hover:bg-gray-200" : "opacity-40 cursor-not-allowed"}`}
                        >
                          <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Linha 2: Datas */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Data de Entrada *</label>
                    <input
                      type="date"
                      name="data_entrada"
                      value={formData.data_entrada}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300 w-64"
                      required
                      disabled={editando}
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">
                      Data de Saída Prevista
                      <span className="text-xs text-gray-400 ml-1">(opcional)</span>
                    </label>
                    <input
                      type="date"
                      name="data_saida_prevista"
                      value={formData.data_saida_prevista}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300 w-64"
                    />
                  </div>
                </div>

                {/* Linha 3: Peso e GMD */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Peso Objetivo *</label>
                    <div className="flex items-center border border-green-500 rounded">
                      <input
                        type="number"
                        name="peso_objetivo"
                        step="0.01"
                        min="1"
                        value={formData.peso_objetivo}
                        onChange={handleChange}
                        className="w-full p-2 rounded-l focus:ring-2 focus:ring-green-300"
                        placeholder="0,00"
                        required
                      />
                      <span className="px-3 text-gray-700 bg-gray-100 rounded-r">kg</span>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">GMD Previsto *</label>
                    <div className="flex items-center border border-green-500 rounded">
                      <input
                        type="number"
                        name="gmd_previsto"
                        step="0.01"
                        min="0.1"
                        value={formData.gmd_previsto}
                        onChange={handleChange}
                        className="w-full p-2 rounded-l focus:ring-2 focus:ring-green-300"
                        placeholder="0,000"
                        required
                      />
                      <span className="px-3 text-gray-700 bg-gray-100 rounded-r">kg/dia</span>
                    </div>
                    <span className="text-xs text-gray-500 mt-1">
                      Ganho Médio Diário esperado
                    </span>
                  </div>
                </div>

                {/* Linha 4: Fase */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Fase *</label>
                  <select
                    name="fase"
                    value={formData.fase}
                    onChange={handleChange}
                    className="border border-green-500 p-2 rounded w-64 focus:ring-2 focus:ring-green-300"
                    required
                  >
                    {fases.map(f => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                  <span className="text-xs text-gray-500 mt-1">
                    {fases.find(f => f.value === formData.fase)?.descricao}
                  </span>
                </div>

                {/* Seleção de Animais (apenas no cadastro) */}
                {!editando && (
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Selecionar Animais *</label>
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="text"
                        placeholder="Pesquisar animal por nome, SISBOV ou brinco..."
                        value={buscaAnimal}
                        onChange={(e) => setBuscaAnimal(e.target.value)}
                        className="flex-1 max-w-md border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                      <button
                          type="button"
                          onClick={() => setMostrarModalAnimal(true)}
                          disabled={!formData.fazenda}
                          title={formData.fazenda ? "Cadastrar novo animal" : "Selecione uma fazenda primeiro"}
                          className={`p-2 rounded ${formData.fazenda ? "hover:bg-gray-200" : "opacity-40 cursor-not-allowed"}`}
                        >
                          <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                    
                    <div className="border border-green-500 rounded p-3 max-h-64 overflow-y-auto">
                      {animaisFiltradosBusca.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">
                          {buscaAnimal ? "Nenhum animal encontrado" : "Nenhum animal disponível para este lote"}
                        </p>
                      ) : (
                        animaisFiltradosBusca.map((animal) => (
                          <label
                            key={animal.id}
                            className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedAnimals.includes(animal.id)}
                              onChange={() => {
                                if (selectedAnimals.includes(animal.id)) {
                                  setSelectedAnimals(prev => prev.filter(id => id !== animal.id));
                                  const newPesoEntrada = { ...formData.pesoEntrada };
                                  delete newPesoEntrada[animal.id];
                                  setFormData(prev => ({ ...prev, pesoEntrada: newPesoEntrada }));
                                } else {
                                  setSelectedAnimals(prev => [...prev, animal.id]);
                                }
                              }}
                            />
                            <div className="flex-1">
                              <span className="font-medium">{animal.nome || "Sem nome"}</span>
                              <span className="text-xs text-gray-500 ml-2">
                                {animal.sisbov} | Brinco: {animal.numero_identificacao}
                              </span>
                            </div>
                            {selectedAnimals.includes(animal.id) && (
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  step="0.01"
                                  placeholder="Peso entrada"
                                  value={formData.pesoEntrada[animal.id] || ""}
                                  onChange={(e) => {
                                    setFormData(prev => ({
                                      ...prev,
                                      pesoEntrada: { ...prev.pesoEntrada, [animal.id]: e.target.value }
                                    }));
                                  }}
                                  className="w-28 p-1 border border-green-500 rounded text-sm"
                                  required
                                />
                                <span className="text-xs">kg</span>
                              </div>
                            )}
                          </label>
                        ))
                      )}
                    </div>
                    <span className="text-sm text-gray-600 mt-2">
                      {selectedAnimals.length} animal(is) selecionado(s)
                    </span>
                  </div>
                )}

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Nome ou Fazenda</label>
                  <input
                    type="text"
                    placeholder="Digite o nome do lote ou fazenda..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
                
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Fase</label>
                  <select
                    value={filtros.fase}
                    onChange={(e) => setFiltros({...filtros, fase: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  >
                    <option value="">Todas as fases</option>
                    <option value="CR">Cria</option>
                    <option value="RC">Recria</option>
                    <option value="EG">Engorda</option>
                  </select>
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Lotes */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">
                  {lotesFiltrados.length} lote(s) encontrado(s)
                </p>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Nome</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Fazenda</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Fase</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Data Entrada</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Saída Prevista</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Animais</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Status</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lotesFiltrados.map((lote) => {
                        const status = getStatusLote(lote);
                        return (
                          <tr key={lote.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                            <td className="p-3 text-sm font-mono">{lote.id}</td>
                            <td className="p-3 text-sm font-medium">{lote.nome}</td>
                            <td className="p-3 text-sm">{lote.fazenda_nome || "-"}</td>
                            <td className="p-3 text-sm">{getFaseLabel(lote.fase)}</td>
                            <td className="p-3 text-sm">
                              {new Date(lote.data_entrada).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                            </td>
                            <td className="p-3 text-sm">
                              {lote.data_saida_prevista
                                ? new Date(lote.data_saida_prevista).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
                                : "-"}
                            </td>
                            <td className="p-3 text-sm">
                              <span className="font-medium">{lote.total_animais || 0}</span>
                              <span className="text-gray-500 text-xs ml-1">
                                ({lote.animais_ativos || 0} ativos)
                              </span>
                            </td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                                {status.label}
                              </span>
                            </td>
                            <td className="p-3">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEditar(lote)}
                                  className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                  title="Editar"
                                >
                                  Editar
                                </button>
                                <button
                                  onClick={() => handleExcluir(lote.id, lote.animais_ativos > 0)}
                                  className={`px-3 py-1 rounded text-sm transition-colors ${
                                    lote.animais_ativos > 0
                                      ? "bg-gray-400 cursor-not-allowed"
                                      : "bg-red-600 hover:bg-red-700 text-white"
                                  }`}
                                  title={lote.animais_ativos > 0 ? "Lote com animais ativos não pode ser excluído" : "Excluir"}
                                  disabled={lote.animais_ativos > 0}
                                >
                                  Excluir
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      
                      {lotesFiltrados.length === 0 && (
                        <tr>
                          <td colSpan="9" className="p-8 text-center text-gray-500">
                            {filtros.busca || filtros.fase
                              ? "Nenhum lote encontrado com os filtros selecionados."
                              : "Nenhum lote cadastrado."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ModalCadastroFazenda
        isOpen={mostrarModalFazenda}
        onClose={() => setMostrarModalFazenda(false)}
        onSuccess={async (nova) => {
          await carregarFazendas();
          setFormData((prev) => ({ ...prev, fazenda: nova.id, pasto: "" }));
        }}
      />
      <ModalCadastroPasto
        isOpen={mostrarModalPasto}
        onClose={() => setMostrarModalPasto(false)}
        fazendaSelecionadaId={formData.fazenda}
        onSuccess={async (novo) => {
          const response = await repositorioPasto.listar(`?fazenda=${formData.fazenda}`);
          setPastos(response.data.results || response.data);
          setFormData((prev) => ({ ...prev, pasto: novo.id }));
        }}
      />
      <ModalCadastroAnimal
        isOpen={mostrarModalAnimal}
        onClose={() => setMostrarModalAnimal(false)}
        fazendaSelecionadaId={formData.fazenda}
        onSuccess={async (novo) => {
          const response = await repositorioAnimal.listar(`?fazenda=${formData.fazenda}`);
          setAllAnimals(response.data.results || response.data);
          setSelectedAnimals((prev) => [...prev, novo.id]);
        }}
      />
    </Layout>
  );
}